import { ComponentMap } from '@cinch-nx/data-storyblok'
import { SbBlokData } from '@storyblok/react'
import React from 'react'
import { logError } from '../helpers/helpers'
import { ComponentLayouts } from '../types'
import { Wrapper } from '../wrapper'

type WrapperType = 'standardWrapper' | 'fullWidthWrapper' | 'slimWrapper'

interface WrapperConfig {
  isFluid?: boolean
  isSlim?: boolean
}

const WRAPPER_CONFIG: Record<WrapperType, WrapperConfig> = {
  standardWrapper: { isFluid: false, isSlim: false },
  fullWidthWrapper: { isFluid: true, isSlim: false },
  slimWrapper: { isFluid: false, isSlim: true },
}

const componentExists = (componentName: string, components: ComponentMap) =>
  !!components[componentName]

const isValidBlock = (block: SbBlokData) => !!block?.component && !!block?._uid

/**
 * Type guard to determine if components is a flat ComponentMap or categorized ComponentLayouts.
 *
 * Note: Component names should not use reserved layout keys:
 * 'noWrapper', 'standardWrapper', 'slimWrapper', 'fullWidthWrapper'
 */
const isComponentMap = (
  components: ComponentLayouts | ComponentMap,
): components is ComponentMap => {
  const layoutKeys = ['noWrapper', ...Object.keys(WRAPPER_CONFIG)]
  const hasLayoutStructure = layoutKeys.some((key) => {
    const value = components[key as keyof typeof components]
    return value && typeof value === 'object' && !React.isValidElement(value)
  })
  return !hasLayoutStructure
}

const createWrappedElement = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.FC<any>,
  block: SbBlokData,
  props?: { [key: string]: unknown },
  config?: WrapperConfig,
): React.JSX.Element => (
  <Wrapper isFluid={config?.isFluid} isSlim={config?.isSlim} key={block._uid}>
    {React.createElement(component, {
      ...block,
      ...props,
    })}
  </Wrapper>
)

const NotCreatedYet: React.FC<{ componentName: string }> = ({
  componentName,
}) => {
  if (process.env.NODE_ENV === 'production') {
    return null
  }
  return <div>The component `{componentName}` has not been created yet.</div>
}

/**
 * Factory function that creates React elements from Storyblok block data by dynamically resolving
 * and rendering the appropriate component based on the block's component name.
 *
 * This factory supports multiple rendering strategies:
 * - Direct component mapping (ComponentMap)
 * - Categorized components with optional wrapper configurations (ComponentLayouts)
 *   - noWrapper: Components rendered without any wrapper
 *   - standardWrapper: Components wrapped with standard Wrapper (1280px max-width)
 *   - slimWrapper: Components wrapped with slim Wrapper (1056px max-width)
 *   - fullWidthWrapper: Components wrapped with fluid Wrapper (full-width)
 *
 * If the block is malformed or the component is not found, appropriate fallbacks are provided.
 *
 * @param block - The Storyblok block data containing component information and unique identifier.
 * Must include 'component' (component name) and '_uid' (unique identifier) properties.
 * @param components - Either a flat ComponentMap or a categorized ComponentLayouts object that maps
 * component names to their React component implementations.
 * @param props - Optional additional properties to be spread into the created component alongside
 * the block data.
 * @returns A React element if the block is valid and a matching component is found, undefined if
 * the block is malformed, or a fallback element displaying a "not created yet" message if the
 * component is not found in the provided map.
 */
export const ElementFactory = (
  block: SbBlokData,
  components: ComponentLayouts | ComponentMap,
  props?: { [key: string]: unknown },
): React.JSX.Element | undefined => {
  if (!isValidBlock(block)) {
    logError(
      "Unable to render malformed block as it is missing the 'component' or '_uid' properties:",
      block,
    )
    return undefined
  }

  if (isComponentMap(components)) {
    if (componentExists(block.component, components)) {
      return React.createElement(components[block.component], {
        ...block,
        ...props,
        key: block._uid,
      })
    }
  } else {
    // Check unwrapped components
    if (
      components.noWrapper &&
      componentExists(block.component, components.noWrapper)
    ) {
      return React.createElement(components.noWrapper[block.component], {
        ...block,
        ...props,
        key: block._uid,
      })
    }

    // Check wrapped components using wrapper configuration
    for (const [wrapperType, wrapperConfig] of Object.entries(WRAPPER_CONFIG)) {
      const componentCategory = components[wrapperType as WrapperType]
      if (
        componentCategory &&
        componentExists(block.component, componentCategory)
      ) {
        return createWrappedElement(
          componentCategory[block.component],
          block,
          props,
          wrapperConfig,
        )
      }
    }
  }

  return React.createElement(NotCreatedYet, {
    componentName: block.component,
    key: block._uid,
  })
}
